import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTasksStore, getLocalDateString } from '@/stores/tasks';
import { useProjectViewStore } from '@/stores/projectView';
import { useAuthStore } from '@/stores/auth';
import { FilterPills } from '@/components/ui/FilterPills';
import { DataTable } from '@/components/tasks/DataTable';
import { TaskCard } from '@/components/tasks/TaskCard';
import type { OdooTask } from '@/types/odoo';

const dateFormatter = new Intl.DateTimeFormat('nl-BE', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

function matchesSearch(task: OdooTask, term: string): boolean {
  const lower = term.toLowerCase();
  if (task.name.toLowerCase().includes(lower)) return true;
  if (task.project_id && task.project_id[1].toLowerCase().includes(lower)) return true;
  if (task.x_studio_afleveradres && task.x_studio_afleveradres[1].toLowerCase().includes(lower)) return true;
  return false;
}

export function TaskListView() {
  const navigate = useNavigate();
  const {
    tasks,
    installerNames,
    projectNames,
    werfAddresses,
    faseSystems,
    loading,
    error,
    filter,
    searchTerm,
    selectedDate,
    fetchTasks,
    setFilter,
    setSearchTerm,
    nextDay,
    prevDay,
    goToToday,
  } = useTasksStore();

  const roles = useAuthStore((s) => s.user?.roles ?? []);
  const isOplijster = roles.includes('oplijster');
  const isProjectleider = roles.includes('projectleider') || roles.includes('admin');

  const isToday = selectedDate === getLocalDateString();

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks, selectedDate]);

  const formattedDate = useMemo(() => {
    const d = new Date(selectedDate + 'T12:00:00');
    return dateFormatter.format(d);
  }, [selectedDate]);

  // Compute stage counts from all tasks
  const stageCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const task of tasks) {
      const stage = task.stage_id ? task.stage_id[1] : 'Onbekend';
      counts[stage] = (counts[stage] ?? 0) + 1;
    }
    return counts;
  }, [tasks]);

  // Build filter pill options
  const filterOptions = useMemo(() => {
    const options: { label: string; count: number }[] = [
      { label: 'Alle', count: tasks.length },
    ];
    const sorted = Object.entries(stageCounts).sort((a, b) => b[1] - a[1]);
    for (const [stage, count] of sorted) {
      options.push({ label: stage, count });
    }
    return options;
  }, [tasks.length, stageCounts]);

  // Filter tasks by stage and search term, then sort by installer (like planning)
  const filteredTasks = useMemo(() => {
    let result = tasks;
    if (filter !== 'Alle') {
      result = result.filter(
        (task) => task.stage_id && task.stage_id[1] === filter,
      );
    }
    if (searchTerm.trim()) {
      result = result.filter((task) => matchesSearch(task, searchTerm.trim()));
    }
    // Sort: tasks without sale order last, then by installer name (alphabetical), unassigned last
    return [...result].sort((a, b) => {
      // Primary: tasks without sale_order_id go last
      const aHasSO = !!(a.sale_order_id && a.sale_order_id[0]);
      const bHasSO = !!(b.sale_order_id && b.sale_order_id[0]);
      if (aHasSO && !bHasSO) return -1;
      if (!aHasSO && bHasSO) return 1;

      // Secondary: sort by installer name, unassigned last
      const aIds: number[] = Array.isArray(a.x_studio_installateur) ? a.x_studio_installateur : [];
      const bIds: number[] = Array.isArray(b.x_studio_installateur) ? b.x_studio_installateur : [];
      const aName = aIds.length > 0 ? (installerNames[aIds[0]] || '') : '';
      const bName = bIds.length > 0 ? (installerNames[bIds[0]] || '') : '';
      if (!aName && bName) return 1;
      if (aName && !bName) return -1;
      return aName.localeCompare(bName, 'nl');
    });
  }, [tasks, filter, searchTerm, installerNames]);

  const setActiveTask = useProjectViewStore((s) => s.setActiveTask);

  const handleTaskClick = (task: OdooTask) => {
    if (!task.project_id) return;
    setActiveTask(task.id);
    navigate(`/project/${task.project_id[0]}?task=${task.id}`);
  };

  return (
    <div>
      {/* Date navigation header */}
      <div className="px-4 lg:px-8 pt-4 lg:pt-6 pb-2 lg:pb-4">
        {/* Fixed-width grid: the arrows sit on the container edges, whatever the label length.
            items-start keeps them on the first line when a long date wraps on a phone. */}
        <div className="grid grid-cols-[2.25rem_1fr_2.25rem] items-start gap-2 w-full max-w-md mx-auto">
          <button
            type="button"
            onClick={prevDay}
            className="flex items-center justify-center w-9 h-9 rounded-full text-td-blue hover:bg-td-blue/10 transition"
            aria-label="Vorige dag"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <h1 className="py-1 text-lg leading-7 lg:text-xl lg:leading-7 font-extrabold text-td-navy capitalize text-center">
            {formattedDate}
          </h1>

          <button
            type="button"
            onClick={nextDay}
            className="flex items-center justify-center w-9 h-9 rounded-full text-td-blue hover:bg-td-blue/10 transition"
            aria-label="Volgende dag"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
        {/* The Vandaag pill lives on this fixed-height line, so it never moves the arrows. */}
        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 h-6 mt-1">
          <span aria-hidden="true" />
          <p className="text-td-muted text-xs lg:text-sm">
            {filteredTasks.length} taken
          </p>
          {!isToday ? (
            <button
              type="button"
              onClick={goToToday}
              className="justify-self-start px-3 py-0.5 text-xs font-semibold bg-td-blue text-white rounded-full hover:bg-td-blue/90 transition"
            >
              Vandaag
            </button>
          ) : (
            <span aria-hidden="true" />
          )}
        </div>
      </div>

      {/* Mobile search */}
      <div className="px-4 pb-3 lg:hidden">
        <div className="relative">
          <svg
            className="absolute left-3 top-2.5 w-4 h-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <label htmlFor="task-search-mobile" className="sr-only">
            Zoek taken
          </label>
          <input
            id="task-search-mobile"
            type="text"
            placeholder="Zoek taak, werf of installateur..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-td-blue focus:border-transparent"
          />
        </div>
      </div>

      {/* Filter pills */}
      <div className="px-4 lg:px-8">
        <FilterPills
          filters={filterOptions}
          active={filter}
          onChange={setFilter}
        />
      </div>

      {/* Content */}
      <div className="px-4 lg:px-8 pb-8 mt-3 lg:mt-5">
        {/* Loading state */}
        {loading && (
          <div className="text-center py-12">
            <div className="inline-block w-8 h-8 border-4 border-td-blue/30 border-t-td-blue rounded-full animate-spin" role="status">
              <span className="sr-only">Laden...</span>
            </div>
            <p className="text-td-muted text-sm mt-3">Taken laden...</p>
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="bg-red-50 text-red-600 p-4 rounded-xl" role="alert">
            <p className="font-semibold">Fout bij laden</p>
            <p className="text-sm mt-1">{error}</p>
          </div>
        )}

        {/* Data */}
        {!loading && !error && (
          <>
            {/* Empty state */}
            {filteredTasks.length === 0 && (
              <div className="text-center py-12">
                <p className="text-td-muted text-sm">
                  Geen taken gevonden
                  {searchTerm && (
                    <span>
                      {' '}
                      voor &quot;{searchTerm}&quot;
                    </span>
                  )}
                  {filter !== 'Alle' && (
                    <span>
                      {' '}
                      in {filter}
                    </span>
                  )}
                </p>
              </div>
            )}

            {/* Desktop table */}
            {filteredTasks.length > 0 && (
              <div className="hidden lg:block">
                {isProjectleider && (
                  <div className="flex items-center gap-4 mb-3 text-xs text-td-muted">
                    <span className="font-semibold uppercase tracking-wider text-td-light">
                      Projectleider
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <span className="w-3.5 h-3.5 rounded bg-blue-50 border border-blue-200" aria-hidden="true" />
                      David
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <span className="w-3.5 h-3.5 rounded bg-gray-100 border border-gray-300" aria-hidden="true" />
                      Jorn
                    </span>
                  </div>
                )}
                <DataTable
                  tasks={filteredTasks}
                  installerNames={installerNames}
                  projectNames={projectNames}
                  werfAddresses={werfAddresses}
                  faseSystems={faseSystems}
                  onTaskClick={(task) => handleTaskClick(task)}
                  showOpgelijst={isOplijster || isProjectleider}
                  showFotos={!isOplijster}
                  showSysteem={isProjectleider || isOplijster}
                />
              </div>
            )}

            {/* Mobile cards */}
            {filteredTasks.length > 0 && (
              <div className="lg:hidden space-y-3 pb-20">
                {filteredTasks.map((task) => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    installerNames={installerNames}
                    projectNames={projectNames}
                    werfAddresses={werfAddresses}
                    faseSystems={faseSystems}
                    onClick={() => handleTaskClick(task)}
                    showOpgelijst={isOplijster || isProjectleider}
                    showFotos={!isOplijster}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
